import React from 'react';

interface GoogleDriveDialogProps {
  isOpen: boolean;
  type: 'confirmation' | 'success' | 'error';
  serialNumber?: string;
  orientation?: 'portrait' | 'landscape';
  googleDriveUrl?: string;
  errorMessage?: string;
  onConfirm?: () => void;
  onCancel: () => void;
}

export const GoogleDriveDialog: React.FC<GoogleDriveDialogProps> = ({
  isOpen,
  type,
  serialNumber,
  orientation,
  googleDriveUrl,
  errorMessage,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  const renderConfirmationDialog = () => (
    <div style={dialogContentStyle}>
      <div style={headerStyle}>
        <span style={iconStyle}>📁</span>
        <h3 style={titleStyle}>Upload to Google Drive</h3>
      </div>
      
      <div style={bodyStyle}>
        <p style={messageStyle}>
          Ready to upload <strong>{serialNumber}</strong> ({orientation}) to Google Drive.
        </p>

        <div style={checklistStyle}>
          <div style={checkItemStyle}>
            <span style={checkboxStyle}>✓</span>
            <span>High-resolution TIFF has been generated</span>
          </div>
          <div style={checkItemStyle}>
            <span style={checkboxStyle}>✓</span>
            <span>The device mapping will be created automatically</span>
          </div>
          <div style={checkItemStyle}>
            <span style={checkboxStyle}>✓</span>
            <span>File will be uploaded to the <strong>{orientation ? orientation.charAt(0).toUpperCase() + orientation.slice(1) : 'selected'}</strong> folder</span>
          </div>
        </div>

        <div style={importantNoteStyle}>
          <p style={importantNoteTextStyle}>
            <strong>📝 Note:</strong> Clicking "Continue" will automatically create the device mapping in the database after uploading to Google Drive.
          </p>
        </div>
      </div>
      
      <div style={actionsStyle}>
        <button
          onClick={onCancel}
          style={cancelButtonStyle}
          onMouseEnter={(e) => {
            (e.target as HTMLButtonElement).style.background = '#e0e0e0';
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLButtonElement).style.background = '#f8f9fa';
          }}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          style={confirmButtonStyle}
          onMouseEnter={(e) => {
            (e.target as HTMLButtonElement).style.background = '#4d00c0';
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLButtonElement).style.background = '#6600ff';
          }}
        >
          Continue
        </button>
      </div>
    </div>
  );

  const renderSuccessDialog = () => (
    <div style={dialogContentStyle}>
      <div style={headerStyle}>
        <span style={successIconStyle}>✅</span>
        <h3 style={titleStyle}>Upload Successful!</h3>
      </div>
      
      <div style={bodyStyle}>
        <p style={messageStyle}>
          <strong>{serialNumber}</strong> has been successfully uploaded to Google Drive and the device mapping has been created.
        </p>

        {googleDriveUrl && (
          <div style={linkContainerStyle}>
            <p style={linkLabelStyle}>View file in Google Drive:</p>
            <a
              href={googleDriveUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={linkStyle}
            >
              Open in Google Drive 🔗
            </a>
          </div>
        )}
      </div>
      
      <div style={actionsStyle}>
        <button
          onClick={onCancel}
          style={confirmButtonStyle}
          onMouseEnter={(e) => {
            (e.target as HTMLButtonElement).style.background = '#4d00c0';
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLButtonElement).style.background = '#6600ff';
          }}
        >
          Close
        </button>
      </div>
    </div>
  );

  const renderErrorDialog = () => (
    <div style={dialogContentStyle}>
      <div style={headerStyle}>
        <span style={errorIconStyle}>❌</span>
        <h3 style={titleStyle}>Upload Failed</h3>
      </div>
      
      <div style={bodyStyle}>
        <p style={messageStyle}>
          Failed to upload <strong>{serialNumber}</strong> to Google Drive.
        </p>
        
        <div style={errorContainerStyle}>
          <p style={errorLabelStyle}>Error details:</p>
          <p style={errorTextStyle}>{errorMessage}</p>
        </div>
        
        <p style={noteStyle}>
          Please check your Google Drive permissions and try again.
        </p>
      </div>
      
      <div style={actionsStyle}>
        <button
          onClick={onCancel}
          style={cancelButtonStyle}
          onMouseEnter={(e) => {
            (e.target as HTMLButtonElement).style.background = '#e0e0e0';
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLButtonElement).style.background = '#f8f9fa';
          }}
        >
          Close
        </button>
      </div>
    </div>
  );

  return (
    <div style={overlayStyle} onClick={onCancel}>
      <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
        {type === 'confirmation' && renderConfirmationDialog()}
        {type === 'success' && renderSuccessDialog()}
        {type === 'error' && renderErrorDialog()}
      </div>
    </div>
  );
};

// Styles
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const dialogStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
  maxWidth: '500px',
  width: '90%',
  maxHeight: '80vh',
  overflow: 'auto',
};

const dialogContentStyle: React.CSSProperties = {
  padding: '24px',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '20px',
};

const iconStyle: React.CSSProperties = {
  fontSize: '32px',
};

const successIconStyle: React.CSSProperties = {
  fontSize: '32px',
};

const errorIconStyle: React.CSSProperties = {
  fontSize: '32px',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '24px',
  fontWeight: '600',
  color: '#333',
};

const bodyStyle: React.CSSProperties = {
  marginBottom: '24px',
};

const messageStyle: React.CSSProperties = {
  fontSize: '16px',
  lineHeight: '1.5',
  color: '#444',
  margin: '0 0 16px 0',
};

const checklistStyle: React.CSSProperties = {
  margin: '16px 0',
  padding: '16px',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px',
  border: '1px solid #e9ecef',
};

const checkItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  margin: '8px 0',
  fontSize: '14px',
  color: '#555',
};

const checkboxStyle: React.CSSProperties = {
  color: '#28a745',
  fontWeight: 'bold',
  fontSize: '16px',
};

const noteStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#666',
  fontStyle: 'italic',
  margin: '16px 0 0 0',
};

const importantNoteStyle: React.CSSProperties = {
  margin: '16px 0 0 0',
  padding: '12px',
  backgroundColor: '#fff3cd',
  borderRadius: '6px',
  border: '1px solid #ffc107',
};

const importantNoteTextStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '14px',
  color: '#856404',
  lineHeight: '1.5',
};

const linkContainerStyle: React.CSSProperties = {
  margin: '16px 0',
  padding: '12px',
  backgroundColor: '#e8f5e8',
  borderRadius: '6px',
  border: '1px solid #c3e6c3',
};

const linkLabelStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#333',
  margin: '0 0 8px 0',
};

const linkStyle: React.CSSProperties = {
  color: '#6600ff',
  textDecoration: 'none',
  fontWeight: '500',
  fontSize: '16px',
};

const errorContainerStyle: React.CSSProperties = {
  margin: '16px 0',
  padding: '12px',
  backgroundColor: '#ffeaa7',
  borderRadius: '6px',
  border: '1px solid #fdcb6e',
};

const errorLabelStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#333',
  margin: '0 0 8px 0',
  fontWeight: '500',
};

const errorTextStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#d63031',
  margin: 0,
  fontFamily: 'monospace',
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
};

const cancelButtonStyle: React.CSSProperties = {
  background: '#f8f9fa',
  color: '#333',
  border: '1px solid #dee2e6',
  padding: '10px 20px',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const confirmButtonStyle: React.CSSProperties = {
  background: '#6600ff',
  color: 'white',
  border: 'none',
  padding: '10px 20px',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s',
};